import { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  AlertCircle,
  Check,
  Edit2,
  FileText,
  Info,
  Link2,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { format } from 'date-fns';
import {
  createPattern,
  createPatternRevision,
  deletePatternRevision,
  findPatternSeed,
  updatePatternRevision,
} from '@/lib/api';
import { getBodyMeasurementValue } from '@/lib/bodyMeasurementFallbacks';

const EMPTY_PATTERN_SEED_STATE = {
  enabled: false,
  sourcePatternCode: '',
  sourcePattern: null,
  seededSpecs: null,
};

const SEED_REFERENCE_SELECTION = '__seed__';

const getSpecsSignature = (specs = {}) => JSON.stringify(
  Object.entries(specs || {})
    .filter(([, value]) => value !== undefined)
    .sort(([left], [right]) => left.localeCompare(right))
);

const formatCompactDate = (value) => {
  if (!value) return '—';

  try {
    return format(new Date(value), 'MMM d, yyyy');
  } catch (_error) {
    return '—';
  }
};

const getDisplayValue = (value, fieldType, unit) => {
  if (value === null || value === undefined || value === '') return '—';
  if (fieldType === 'number') return `${value} ${unit}`;
  return String(value);
};

const buildRevisionPatternCode = (basePatternCode, revisionNumber, patterns = []) => {
  const normalizedBase = String(basePatternCode || '').trim();
  if (!normalizedBase) return `V${revisionNumber}`;

  const prefix = normalizedBase.replace(/-\d+$/, '');
  const exactCode = `${prefix}-${revisionNumber}`;
  const matchedPattern = patterns.find((pattern) => pattern.pattern_code === exactCode);

  return matchedPattern?.pattern_code || exactCode;
};

const PatternTab = ({
  customerId,
  customer,
  garmentType,
  patterns,
  revisionsByPattern,
  specFields,
  allMeasurements,
  onRefresh,
  onRevisionSynced,
  onHeaderConfigChange,
}) => {
  const [selectedPattern, setSelectedPattern] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [currentWorkingRevisionId, setCurrentWorkingRevisionId] = useState(null);
  const [referenceSelection, setReferenceSelection] = useState(null);
  const [selectedBodyMeasurementId, setSelectedBodyMeasurementId] = useState(null);
  const [workingDraftSpecs, setWorkingDraftSpecs] = useState({});
  const [activeCellFieldKey, setActiveCellFieldKey] = useState(null);
  const [autosaveState, setAutosaveState] = useState('idle');
  const [autosaveMessage, setAutosaveMessage] = useState('All changes saved');
  const [editingVersionNumberId, setEditingVersionNumberId] = useState(null);
  const [tempVersionNumber, setTempVersionNumber] = useState('');
  const [rightSidebarOpen, setRightSidebarOpen] = useState(true);
  const [selectedRevisionForSidebar, setSelectedRevisionForSidebar] = useState(null);
  const [editingSidebarNotes, setEditingSidebarNotes] = useState(false);
  const [sidebarNotesValue, setSidebarNotesValue] = useState('');
  const [newPatternDialogOpen, setNewPatternDialogOpen] = useState(false);
  const [newRevisionDialogOpen, setNewRevisionDialogOpen] = useState(false);
  const [editNotesDialogOpen, setEditNotesDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [revisionToDelete, setRevisionToDelete] = useState(null);
  const [revisionToEditNotes, setRevisionToEditNotes] = useState(null);
  const [editedNotes, setEditedNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [stickyOffset, setStickyOffset] = useState(0);

  const [patternForm, setPatternForm] = useState({
    customVersionNumber: '',
    description: '',
  });
  const [patternSeed, setPatternSeed] = useState(EMPTY_PATTERN_SEED_STATE);

  const [revisionForm, setRevisionForm] = useState({
    customVersionNumber: '',
    revision_notes: '',
    garment_specifications: {},
    copyFromRevisionId: null,
  });

  const revisionsRef = useRef([]);
  const workingDraftSpecsRef = useRef({});
  const lastSavedSignaturesRef = useRef({});
  const autosaveQueueRef = useRef(null);
  const autosaveInFlightRef = useRef(false);
  const autosaveTimerRef = useRef(null);
  const cellInputRefs = useRef({});

  useEffect(() => {
    revisionsRef.current = revisions;
  }, [revisions]);

  useEffect(() => () => {
    if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current);
    }
  }, []);

  useEffect(() => {
    const updateStickyOffset = () => {
      const pageHeader = document.querySelector('[data-testid="fitting-studio-page-header"]');
      setStickyOffset(pageHeader?.getBoundingClientRect?.().height || 0);
    };

    updateStickyOffset();
    window.addEventListener('resize', updateStickyOffset);

    return () => window.removeEventListener('resize', updateStickyOffset);
  }, []);

  // The table needs its OWN bounded scroll viewport (rather than relying on page scroll)
  // because a horizontally-scrollable ancestor (overflow-x: auto) forces the browser to
  // compute overflow-y as "auto" as well (per CSS overflow spec), which silently breaks
  // position:sticky for any descendant relying on window/page scroll. Giving this table a
  // fixed max-height + overflow-y:auto makes it the genuine scroll container, so the header
  // can reliably stick at top:0 within it (true "frozen header" spreadsheet behavior).
  const [tableMaxHeight, setTableMaxHeight] = useState('70vh');

  useEffect(() => {
    const updateTableMaxHeight = () => {
      const pageHeader = document.querySelector('[data-testid="fitting-studio-page-header"]');
      const headerHeight = pageHeader?.getBoundingClientRect?.().height || 0;
      // Reserve space for the page header + content container's top/bottom padding + a small safety buffer
      setTableMaxHeight(`calc(100vh - ${headerHeight}px - 48px)`);
    };

    updateTableMaxHeight();
    window.addEventListener('resize', updateTableMaxHeight);

    return () => window.removeEventListener('resize', updateTableMaxHeight);
  }, []);

  useEffect(() => {
    if (!patterns?.length) {
      setSelectedPattern(null);
      setRevisions([]);
      setCurrentWorkingRevisionId(null);
      setReferenceSelection(null);
      return;
    }

    const nextPattern = selectedPattern && patterns.some((pattern) => pattern.pattern_id === selectedPattern.pattern_id)
      ? patterns.find((pattern) => pattern.pattern_id === selectedPattern.pattern_id)
      : patterns[0];

    const nextRevisions = revisionsByPattern[nextPattern.pattern_id] || [];
    setSelectedPattern(nextPattern);
    setRevisions(nextRevisions);
  }, [patterns, revisionsByPattern, selectedPattern]);

  useEffect(() => {
    if (!revisions.length) return;

    const currentStillExists = revisions.some((revision) => revision.revision_id === currentWorkingRevisionId);
    if (!currentStillExists) {
      setCurrentWorkingRevisionId(revisions[0].revision_id);
    }
  }, [revisions, currentWorkingRevisionId]);

  const workingRevision = currentWorkingRevisionId
    ? revisions.find((revision) => revision.revision_id === currentWorkingRevisionId)
    : revisions[0];

  const seedReference = workingRevision?.seed_pattern_snapshot
    ? {
        reference_id: SEED_REFERENCE_SELECTION,
        reference_type: 'seed',
        pattern_code: workingRevision?.seed_pattern_code,
        created_at: workingRevision?.seed_pattern_created_at,
        garment_specifications: workingRevision?.seed_pattern_snapshot || {},
      }
    : null;

  const revisionReferenceOptions = revisions.filter((revision) => revision.revision_id !== currentWorkingRevisionId);

  useEffect(() => {
    if (!workingRevision) {
      setReferenceSelection(null);
      return;
    }

    const hasSeedReference = Boolean(seedReference);
    const revisionSelectionStillExists = referenceSelection && revisionReferenceOptions.some((revision) => revision.revision_id === referenceSelection);
    const isSeedSelection = referenceSelection === SEED_REFERENCE_SELECTION;

    if ((isSeedSelection && hasSeedReference) || revisionSelectionStillExists) {
      return;
    }

    if (hasSeedReference) {
      setReferenceSelection(SEED_REFERENCE_SELECTION);
      return;
    }

    setReferenceSelection(revisionReferenceOptions[0]?.revision_id || null);
  }, [workingRevision?.revision_id, referenceSelection, revisionReferenceOptions, seedReference]);

  const referenceRevision = referenceSelection && referenceSelection !== SEED_REFERENCE_SELECTION
    ? revisions.find((revision) => revision.revision_id === referenceSelection)
    : null;

  const selectedReference = referenceSelection === SEED_REFERENCE_SELECTION
    ? seedReference
    : referenceRevision
      ? {
          reference_id: referenceRevision.revision_id,
          reference_type: 'revision',
          pattern_code: buildRevisionPatternCode(
            selectedPattern?.pattern_code,
            referenceRevision?.revision_number,
            patterns,
          ),
          created_at: referenceRevision.created_at,
          garment_specifications: referenceRevision.garment_specifications || {},
        }
      : null;

  useEffect(() => {
    if (!workingRevision) return;

    const nextDraft = { ...(workingRevision.garment_specifications || {}) };
    setWorkingDraftSpecs(nextDraft);
    workingDraftSpecsRef.current = nextDraft;
    lastSavedSignaturesRef.current[workingRevision.revision_id] = getSpecsSignature(nextDraft);
    setActiveCellFieldKey(null);
    setAutosaveState('idle');
    setAutosaveMessage('All changes saved');
  }, [workingRevision?.revision_id, workingRevision?.updated_at]);

  useEffect(() => {
    if (workingRevision?.linked_measurement_id) {
      setSelectedBodyMeasurementId(workingRevision.linked_measurement_id);
      return;
    }

    if (allMeasurements?.length) {
      setSelectedBodyMeasurementId(allMeasurements[0].measurement_id);
    }
  }, [workingRevision?.revision_id, workingRevision?.linked_measurement_id, allMeasurements]);

  useEffect(() => {
    if (!workingRevision) {
      setSelectedRevisionForSidebar(null);
      return;
    }

    const currentSidebarRevisionStillExists = selectedRevisionForSidebar
      && revisions.some((revision) => revision.revision_id === selectedRevisionForSidebar.revision_id);

    if (!currentSidebarRevisionStillExists) {
      setSelectedRevisionForSidebar(workingRevision);
    }
  }, [workingRevision, revisions, selectedRevisionForSidebar]);

  const updateRevisionInState = (revisionId, patch) => {
    setRevisions((previousRevisions) => previousRevisions
      .map((revision) => (revision.revision_id === revisionId ? { ...revision, ...patch } : revision))
      .sort((left, right) => right.revision_number - left.revision_number));

    setSelectedRevisionForSidebar((previousRevision) => (
      previousRevision?.revision_id === revisionId ? { ...previousRevision, ...patch } : previousRevision
    ));

    // Keep the parent page's cached revisionsByPattern in sync immediately so that
    // switching garment-type tabs (which unmounts/remounts this component) and coming
    // back does not re-derive from stale pre-edit data - only a hard page refresh
    // should ever be needed to see saved changes, and it shouldn't be needed at all.
    onRevisionSynced?.(revisionId, patch);
  };

  const setAutosaveFeedback = (state, message) => {
    if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current);
    }

    setAutosaveState(state);
    setAutosaveMessage(message);

    if (state === 'saved') {
      autosaveTimerRef.current = window.setTimeout(() => {
        setAutosaveState('idle');
        setAutosaveMessage('All changes saved');
      }, 1400);
    }
  };

  const flushAutosaveQueue = async () => {
    if (autosaveInFlightRef.current || !autosaveQueueRef.current) return;

    const queuedSave = autosaveQueueRef.current;
    autosaveQueueRef.current = null;

    const revision = revisionsRef.current.find((item) => item.revision_id === queuedSave.revisionId);
    if (!revision) return;

    autosaveInFlightRef.current = true;
    setAutosaveFeedback('saving', `Saving ${queuedSave.fieldLabel}...`);

    try {
      const response = await updatePatternRevision(queuedSave.revisionId, {
        pattern_id: revision.pattern_id,
        revision_notes: revision.revision_notes,
        linked_measurement_id: revision.linked_measurement_id,
        garment_specifications: queuedSave.specs,
        status: revision.status,
      });

      lastSavedSignaturesRef.current[queuedSave.revisionId] = getSpecsSignature(queuedSave.specs);
      updateRevisionInState(queuedSave.revisionId, {
        garment_specifications: queuedSave.specs,
        updated_at: response?.data?.updated_at || new Date().toISOString(),
      });
      setAutosaveFeedback('saved', `${queuedSave.fieldLabel} saved`);
    } catch (error) {
      console.error('Pattern autosave failed:', error);
      setAutosaveFeedback('error', `Could not save ${queuedSave.fieldLabel}`);
      toast.error(`Failed to save ${queuedSave.fieldLabel}`);
    } finally {
      autosaveInFlightRef.current = false;
      if (autosaveQueueRef.current) {
        flushAutosaveQueue();
      }
    }
  };

  const queueAutosave = (field) => {
    if (!workingRevision) return;

    const nextSpecs = { ...workingDraftSpecsRef.current };
    const nextSignature = getSpecsSignature(nextSpecs);
    const lastSavedSignature = lastSavedSignaturesRef.current[workingRevision.revision_id];

    if (nextSignature === lastSavedSignature) {
      return;
    }

    autosaveQueueRef.current = {
      revisionId: workingRevision.revision_id,
      specs: nextSpecs,
      fieldLabel: field.field_label,
    };

    flushAutosaveQueue();
  };

  const handleWorkingSpecChange = (fieldKey, value) => {
    setWorkingDraftSpecs((previousDraft) => {
      const nextDraft = {
        ...previousDraft,
        [fieldKey]: value,
      };

      workingDraftSpecsRef.current = nextDraft;
      return nextDraft;
    });
  };

  const handleWorkingCellKeyDown = (event, fieldIndex) => {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    const direction = event.shiftKey ? -1 : 1;
    const nextField = specFields[fieldIndex + direction];

    if (!nextField) return;

    cellInputRefs.current[nextField.field_key]?.focus();
    cellInputRefs.current[nextField.field_key]?.select?.();
  };

  const openNewRevisionDialog = useCallback(() => {
    setRevisionForm({
      customVersionNumber: '',
      revision_notes: '',
      garment_specifications: workingRevision ? { ...workingRevision.garment_specifications } : {},
      copyFromRevisionId: workingRevision?.revision_id || null,
    });
    setNewRevisionDialogOpen(true);
  }, [workingRevision]);

  const handleCreatePattern = async () => {
    try {
      setSaving(true);

      const patternCode = `${garmentType.prefix}${customer.customer_no || customer.customer_number || '1'}-${patternForm.customVersionNumber || '1'}`;
      const linkedMeasurementId = selectedBodyMeasurementId || allMeasurements?.[0]?.measurement_id || null;

      await createPattern({
        customer_id: customerId,
        garment_type: garmentType.key,
        notes: patternForm.description,
        pattern_code: patternCode,
        linked_measurement_id: linkedMeasurementId,
        garment_specifications: patternSeed.seededSpecs ? { ...patternSeed.seededSpecs } : {},
        seed_pattern_id: patternSeed.sourcePattern?.pattern_id || null,
        seed_pattern_code: patternSeed.sourcePattern?.pattern_code || null,
      });

      toast.success(patternSeed.sourcePattern ? `Pattern created and seeded from ${patternSeed.sourcePattern.pattern_code}` : 'Pattern created successfully');
      setNewPatternDialogOpen(false);
      setPatternForm({ customVersionNumber: '', description: '' });
      setPatternSeed(EMPTY_PATTERN_SEED_STATE);
      onRefresh();
    } catch (error) {
      console.error('Pattern creation error:', error);
      toast.error('Failed to create pattern');
    } finally {
      setSaving(false);
    }
  };

  const handlePatternSeedCodeChange = (value) => {
    setPatternSeed((previousSeed) => ({
      ...previousSeed,
      sourcePatternCode: value,
      sourcePattern: null,
      seededSpecs: null,
    }));
  };

  const handleLoadPatternSeed = async () => {
    const requestedPatternCode = String(patternSeed.sourcePatternCode || '').trim();
    if (!requestedPatternCode) {
      toast.error('Enter a pattern number to seed from');
      return;
    }

    try {
      setSaving(true);
      const response = await findPatternSeed(requestedPatternCode, garmentType.key);
      const matchedPattern = (response?.data || [])[0];

      if (!matchedPattern) {
        toast.error(`No ${garmentType.label.toLowerCase()} pattern found for ${requestedPatternCode}`);
        setPatternSeed((previousSeed) => ({
          ...previousSeed,
          sourcePattern: null,
          seededSpecs: null,
        }));
        return;
      }

      const nextSeededSpecs = { ...(matchedPattern.garment_specifications || {}) };
      if (Object.keys(nextSeededSpecs).length === 0) {
        toast.error(`${matchedPattern.pattern_code} has no measurement/spec values to seed`);
        setPatternSeed((previousSeed) => ({
          ...previousSeed,
          sourcePattern: null,
          seededSpecs: null,
        }));
        return;
      }

      setPatternSeed((previousSeed) => ({
        ...previousSeed,
        sourcePattern: matchedPattern,
        seededSpecs: nextSeededSpecs,
      }));
      toast.success(`Loaded seed from ${matchedPattern.pattern_code}`);
    } catch (error) {
      console.error('Pattern seed lookup error:', error);
      toast.error(error.response?.data?.detail || `Could not find ${garmentType.label.toLowerCase()} pattern ${requestedPatternCode}`);
      setPatternSeed((previousSeed) => ({
        ...previousSeed,
        sourcePattern: null,
        seededSpecs: null,
      }));
    } finally {
      setSaving(false);
    }
  };

  const handleResetPatternSeed = () => {
    setPatternSeed(EMPTY_PATTERN_SEED_STATE);
  };

  const handlePatternDialogOpenChange = (open) => {
    setNewPatternDialogOpen(open);
    if (!open) {
      setPatternForm({ customVersionNumber: '', description: '' });
      setPatternSeed(EMPTY_PATTERN_SEED_STATE);
    }
  };

  const renderPatternSeedSection = () => (
    <div className="space-y-3 rounded-xl border border-sky-200/80 bg-sky-50/80 p-3" data-testid="pattern-seed-section">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <p className="text-sm font-medium text-sky-950">Seed from an existing {garmentType.label.toLowerCase()} pattern</p>
          <p className="text-xs text-sky-800">
            Copy measurement/spec fields only. Notes stay with the new client pattern.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleResetPatternSeed}
          disabled={saving}
          data-testid="pattern-seed-clear-button"
        >
          Clear
        </Button>
      </div>

      <div className="space-y-2">
        <Label htmlFor={`pattern-seed-source-${garmentType.key}`}>Seed Pattern Number</Label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input
            id={`pattern-seed-source-${garmentType.key}`}
            placeholder={`e.g. ${garmentType.prefix}${customer.customer_no || customer.customer_number || '3864'}-1`}
            value={patternSeed.sourcePatternCode}
            onChange={(event) => handlePatternSeedCodeChange(event.target.value)}
            data-testid="pattern-seed-source-input"
          />
          <Button
            type="button"
            variant="secondary"
            className="sm:min-w-[132px]"
            onClick={handleLoadPatternSeed}
            disabled={saving}
            data-testid="pattern-seed-load-button"
          >
            <Search className="mr-2 h-4 w-4" />
            Use Seed
          </Button>
        </div>
      </div>

      {patternSeed.sourcePattern && patternSeed.seededSpecs && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900" data-testid="pattern-seed-loaded-summary">
          Loaded <span className="font-semibold">{patternSeed.sourcePattern.pattern_code}</span> · {Object.keys(patternSeed.seededSpecs).length} measurement/spec fields ready to copy.
        </div>
      )}
    </div>
  );

  const renderCreatePatternDialog = ({
    title,
    description,
    numberInputLabel,
    numberInputTestId,
    notesInputLabel,
    notesInputTestId,
    submitButtonTestId,
  }) => (
    <Dialog open={newPatternDialogOpen} onOpenChange={handlePatternDialogOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>{numberInputLabel}</Label>
            <Input
              type="number"
              placeholder="1"
              value={patternForm.customVersionNumber}
              onChange={(event) => setPatternForm({ ...patternForm, customVersionNumber: event.target.value })}
              className="mt-1"
              data-testid={numberInputTestId}
            />
          </div>
          <div>
            <Label>{notesInputLabel}</Label>
            <Textarea
              placeholder="Notes about this pattern..."
              value={patternForm.description}
              onChange={(event) => setPatternForm({ ...patternForm, description: event.target.value })}
              rows={3}
              className="mt-1"
              data-testid={notesInputTestId}
            />
          </div>
          {patternSeed.enabled ? (
            renderPatternSeedSection()
          ) : (
            <Button
              type="button"
              variant="outline"
              className="w-full justify-center gap-2"
              onClick={() => setPatternSeed((previousSeed) => ({ ...previousSeed, enabled: true }))}
              data-testid="pattern-seed-toggle-button"
            >
              <Link2 className="h-4 w-4" />
              Seed from existing {garmentType.label.toLowerCase()} pattern
            </Button>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handlePatternDialogOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleCreatePattern} disabled={saving} data-testid={submitButtonTestId}>
            {saving ? 'Creating...' : 'Create Pattern'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const handleCreateRevision = async () => {
    if (!selectedPattern) return;

    try {
      setSaving(true);

      const existingVersionNumbers = revisions.map((revision) => revision.revision_number);
      const customVersion = parseInt(revisionForm.customVersionNumber, 10);

      if (customVersion && existingVersionNumbers.includes(customVersion)) {
        toast.error(`Version ${customVersion} already exists`);
        return;
      }

      const linkedMeasurementId = selectedBodyMeasurementId || allMeasurements?.[0]?.measurement_id || null;

      await createPatternRevision({
        pattern_id: selectedPattern.pattern_id,
        revision_notes: revisionForm.revision_notes,
        linked_measurement_id: linkedMeasurementId,
        garment_specifications: revisionForm.garment_specifications,
        custom_version_number: customVersion || undefined,
      });

      toast.success('New revision created');
      setNewRevisionDialogOpen(false);
      setRevisionForm({
        customVersionNumber: '',
        revision_notes: '',
        garment_specifications: {},
        copyFromRevisionId: null,
      });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create revision');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMeasurementLink = async (revision, nextMeasurementId) => {
    if (!revision || !nextMeasurementId) {
      toast.error('Please select a body measurement');
      return;
    }

    try {
      setSaving(true);
      const response = await updatePatternRevision(revision.revision_id, {
        pattern_id: revision.pattern_id,
        revision_notes: revision.revision_notes,
        linked_measurement_id: nextMeasurementId,
        garment_specifications: revision.garment_specifications,
        status: revision.status,
      });

      const linkedMeasurement = allMeasurements?.find((measurement) => measurement.measurement_id === nextMeasurementId);

      updateRevisionInState(revision.revision_id, {
        linked_measurement_id: nextMeasurementId,
        body_measurement_snapshot: response?.data?.body_measurement_snapshot || revision.body_measurement_snapshot,
        updated_at: response?.data?.updated_at || new Date().toISOString(),
      });

      if (revision.revision_id === currentWorkingRevisionId) {
        setSelectedBodyMeasurementId(nextMeasurementId);
      }

      toast.success(`Measurement linked: ${linkedMeasurement ? formatCompactDate(linkedMeasurement.measurement_date) : 'Unknown date'}`);
    } catch (error) {
      console.error('Link measurement error:', error);
      toast.error('Failed to link measurement');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenEditNotes = (revision) => {
    setRevisionToEditNotes(revision);
    setEditedNotes(revision.revision_notes || '');
    setEditNotesDialogOpen(true);
  };

  const handleSaveNotes = async () => {
    if (!revisionToEditNotes) return;

    try {
      setSaving(true);
      const response = await updatePatternRevision(revisionToEditNotes.revision_id, {
        pattern_id: revisionToEditNotes.pattern_id,
        revision_notes: editedNotes,
        linked_measurement_id: revisionToEditNotes.linked_measurement_id,
        garment_specifications: revisionToEditNotes.garment_specifications,
        status: revisionToEditNotes.status,
      });

      updateRevisionInState(revisionToEditNotes.revision_id, {
        revision_notes: editedNotes,
        updated_at: response?.data?.updated_at || new Date().toISOString(),
      });

      toast.success('Notes updated');
      setEditNotesDialogOpen(false);
      setRevisionToEditNotes(null);
    } catch (error) {
      toast.error('Failed to update notes');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSidebarNotes = async () => {
    if (!selectedRevisionForSidebar) return;

    try {
      setSaving(true);
      const response = await updatePatternRevision(selectedRevisionForSidebar.revision_id, {
        pattern_id: selectedRevisionForSidebar.pattern_id,
        revision_notes: sidebarNotesValue,
        linked_measurement_id: selectedRevisionForSidebar.linked_measurement_id,
        garment_specifications: selectedRevisionForSidebar.garment_specifications,
      });

      updateRevisionInState(selectedRevisionForSidebar.revision_id, {
        revision_notes: sidebarNotesValue,
        updated_at: response?.data?.updated_at || new Date().toISOString(),
      });

      toast.success('Notes updated');
      setEditingSidebarNotes(false);
    } catch (error) {
      console.error('Save sidebar notes error:', error);
      toast.error('Failed to update notes');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteConfirm = (revision) => {
    setRevisionToDelete(revision);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteRevision = async () => {
    if (!revisionToDelete) return;

    try {
      setSaving(true);
      await deletePatternRevision(revisionToDelete.revision_id);

      toast.success('Revision deleted');
      setDeleteConfirmOpen(false);
      setRevisionToDelete(null);
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete revision');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveVersionNumber = async (revision) => {
    const newVersionNumber = parseInt(tempVersionNumber, 10);

    if (Number.isNaN(newVersionNumber) || newVersionNumber < 1) {
      toast.error('Version number must be a positive integer');
      return;
    }

    const isDuplicate = revisions.some(
      (item) => item.revision_id !== revision.revision_id && item.revision_number === newVersionNumber,
    );

    if (isDuplicate) {
      toast.error(`Version ${newVersionNumber} already exists`);
      return;
    }

    try {
      setSaving(true);
      const response = await updatePatternRevision(revision.revision_id, {
        pattern_id: revision.pattern_id,
        revision_notes: revision.revision_notes,
        linked_measurement_id: revision.linked_measurement_id,
        garment_specifications: revision.garment_specifications,
        status: revision.status,
        revision_number: newVersionNumber,
      });

      updateRevisionInState(revision.revision_id, {
        revision_number: newVersionNumber,
        updated_at: response?.data?.updated_at || new Date().toISOString(),
      });

      toast.success(`Version number updated to ${newVersionNumber}`);
      setEditingVersionNumberId(null);
      setTempVersionNumber('');
    } catch (error) {
      toast.error('Failed to update version number');
    } finally {
      setSaving(false);
    }
  };

  const getValueDiff = (currentValue, previousValue) => {
    if (!previousValue || !currentValue) return null;

    const diff = parseFloat(currentValue) - parseFloat(previousValue);
    if (Number.isNaN(diff) || diff === 0) return null;
    return diff > 0 ? `+${diff.toFixed(2)}` : diff.toFixed(2);
  };

  const selectedBodyMeasurement = selectedBodyMeasurementId
    ? allMeasurements?.find((measurement) => measurement.measurement_id === selectedBodyMeasurementId)
    : allMeasurements?.[0];

  const measurementDeleted = !selectedBodyMeasurement && Boolean(workingRevision?.body_measurement_snapshot);
  const bodyMeasurementForDisplay = measurementDeleted
    ? workingRevision?.body_measurement_snapshot
    : selectedBodyMeasurement;
  const unit = bodyMeasurementForDisplay?.unit === 'inches' ? 'in' : bodyMeasurementForDisplay?.unit || 'in';
  const bodySelectorValue = measurementDeleted
    ? '__snapshot__'
    : (selectedBodyMeasurementId || allMeasurements?.[0]?.measurement_id || '__none__');

  const getRevisionPatternCode = (revision) => buildRevisionPatternCode(
    selectedPattern?.pattern_code,
    revision?.revision_number,
    patterns,
  );

  const currentPatternCode = workingRevision ? getRevisionPatternCode(workingRevision) : '';
  const referencePatternCode = selectedReference?.pattern_code || '';
  const referenceColumnLabel = selectedReference?.reference_type === 'seed' ? 'Seed Reference' : 'Reference';
  // Use minmax()/fr tracks (instead of fixed px) so columns stretch to fill the full
  // available width on wide screens (no dead blank column) while still guaranteeing
  // enough room for the "Reference · <date>" header label to stay on one line, and
  // still respecting a sane minimum for horizontal-scroll fallback on narrow screens.
  const gridColumns = selectedReference
    ? 'minmax(260px,1.3fr) minmax(150px,0.8fr) minmax(220px,1.1fr) minmax(230px,1.15fr)'
    : 'minmax(280px,1.6fr) minmax(160px,1fr) minmax(240px,1.4fr)';
  const gridMinWidth = selectedReference ? '860px' : '680px';

  useEffect(() => {
    if (!onHeaderConfigChange) return undefined;

    if (!selectedPattern) {
      onHeaderConfigChange(null);
      return undefined;
    }

    onHeaderConfigChange({
      patternLabel: `${garmentType.label} Pattern ${currentPatternCode || selectedPattern.pattern_code}`,
      saveState: autosaveState,
      saveMessage: autosaveMessage,
      onNewRevision: openNewRevisionDialog,
    });

    return () => onHeaderConfigChange(null);
  }, [
    onHeaderConfigChange,
    selectedPattern,
    garmentType.label,
    currentPatternCode,
    autosaveState,
    autosaveMessage,
    openNewRevisionDialog,
  ]);

  if (!patterns || patterns.length === 0) {
    return (
      <>
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <div className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full ${garmentType.color} text-3xl`}>
                {garmentType.icon}
              </div>
              <p className="mb-2 text-lg font-medium">No {garmentType.label} Patterns Yet</p>
              <p className="mb-4 text-sm text-muted-foreground">
                Create the first {garmentType.label.toLowerCase()} pattern for {customer.customer_name || customer.name}
              </p>
              <Button onClick={() => setNewPatternDialogOpen(true)} data-testid="pattern-empty-create-button">
                <Plus className="mr-2 h-4 w-4" />
                Create {garmentType.label} Pattern
              </Button>
            </div>
          </CardContent>
        </Card>

        {renderCreatePatternDialog({
          title: `Create ${garmentType.label} Pattern`,
          description: `Creates pattern ${garmentType.prefix}${customer.customer_no || customer.customer_number || '1'}-${patternForm.customVersionNumber || '1'}.`,
          numberInputLabel: 'Pattern Number (Optional)',
          numberInputTestId: 'pattern-create-number-input',
          notesInputLabel: 'Notes (Optional)',
          notesInputTestId: 'pattern-create-notes-input',
          submitButtonTestId: 'pattern-create-submit-button',
        })}
      </>
    );
  }

  if (selectedPattern && revisions.length === 0) {
    return (
      <>
        <Card>
          <CardContent className="py-14">
            <div className="space-y-4 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
                <span className="text-3xl">{garmentType.icon}</span>
              </div>
              <h3 className="text-xl font-semibold">No Pattern Versions Yet</h3>
              <p className="mx-auto max-w-md text-muted-foreground">
                Pattern {selectedPattern.pattern_code} exists but needs its first working version.
              </p>
              <Button onClick={() => setNewRevisionDialogOpen(true)} data-testid="pattern-create-first-revision-button">
                <Plus className="mr-2 h-4 w-4" />
                Create First Version
              </Button>
            </div>
          </CardContent>
        </Card>

        <Dialog open={newRevisionDialogOpen} onOpenChange={setNewRevisionDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create New Revision</DialogTitle>
              <DialogDescription>
                Create the first working version for this pattern.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">
                This version will open directly in the compact compare-and-edit grid.
              </div>
              <div>
                <Label>Revision Notes (Optional)</Label>
                <Textarea
                  placeholder="Notes about this version..."
                  value={revisionForm.revision_notes}
                  onChange={(event) => setRevisionForm({ ...revisionForm, revision_notes: event.target.value })}
                  rows={3}
                  className="mt-1"
                  data-testid="pattern-first-revision-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNewRevisionDialogOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={handleCreateRevision} disabled={saving} data-testid="pattern-first-revision-submit-button">
                {saving ? 'Creating...' : 'Create Revision'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <Card className="overflow-hidden" data-testid="pattern-compare-grid-card">
            <CardContent className="p-0">
              <div
                className="overflow-auto"
                style={{ maxHeight: tableMaxHeight }}
                data-testid="pattern-compare-grid-scroll-viewport"
              >
                <div className="min-w-[680px]" style={{ minWidth: gridMinWidth }} data-testid="pattern-compare-grid-table">
                  <div
                    className="sticky top-0 z-30 border-b bg-white shadow-sm"
                    data-testid="pattern-sticky-header"
                  >
                    <div className="grid" style={{ gridTemplateColumns: gridColumns }}>
                      <div className="border-r bg-slate-100 px-3 py-1.5 text-center text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-600">
                        Specification
                      </div>
                      <div className="border-r bg-sky-50 px-2 py-1.5 text-center text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-600">
                        <div className="flex flex-col items-center gap-0.5">
                          <div>Body</div>
                          <Select
                            value={bodySelectorValue}
                            onValueChange={(value) => {
                              if (value === '__snapshot__' || value === '__none__') return;
                              handleSaveMeasurementLink(workingRevision, value);
                            }}
                            disabled={!workingRevision}
                          >
                            <SelectTrigger className="h-6 w-[128px] justify-center bg-white text-[11px]" data-testid="pattern-toolbar-body-set-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {measurementDeleted && (
                                <SelectItem value="__snapshot__" disabled>
                                  Snapshot • {formatCompactDate(bodyMeasurementForDisplay?.measurement_date)}
                                </SelectItem>
                              )}
                              {allMeasurements?.map((measurement, index) => (
                                <SelectItem key={measurement.measurement_id} value={measurement.measurement_id}>
                                  {index === 0 ? 'Latest • ' : ''}
                                  {formatCompactDate(measurement.measurement_date)}
                                </SelectItem>
                              ))}
                              {!allMeasurements?.length && (
                                <SelectItem value="__none__" disabled>No body measurements</SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="border-r bg-emerald-50 px-2 py-1.5 text-center text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-600">
                        <div className="flex flex-col items-center gap-0.5">
                          <div className="flex items-center gap-1 whitespace-nowrap text-[10px] uppercase tracking-[0.16em] text-slate-600">
                            <span>Current · {formatCompactDate(workingRevision?.created_at)}</span>
                          </div>
                          <div className="flex items-center justify-center gap-1">
                            {editingVersionNumberId === workingRevision?.revision_id ? (
                              <div className="flex items-center gap-1">
                                <Input
                                  type="number"
                                  min="1"
                                  value={tempVersionNumber}
                                  onChange={(event) => setTempVersionNumber(event.target.value)}
                                  className="h-6 w-14 bg-white text-center text-[11px]"
                                  autoFocus
                                  data-testid="pattern-current-version-number-input"
                                />
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-5 w-5 p-0"
                                  onClick={() => handleSaveVersionNumber(workingRevision)}
                                  data-testid="pattern-current-version-save-button"
                                >
                                  <Check className="h-3 w-3 text-emerald-600" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-5 w-5 p-0"
                                  onClick={() => {
                                    setEditingVersionNumberId(null);
                                    setTempVersionNumber('');
                                  }}
                                  data-testid="pattern-current-version-cancel-button"
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            ) : (
                              <>
                                <Select value={currentWorkingRevisionId || ''} onValueChange={setCurrentWorkingRevisionId}>
                                  <SelectTrigger className="h-6 w-[118px] justify-center bg-white text-[11px]" data-testid="pattern-toolbar-working-version-select">
                                    <SelectValue placeholder={currentPatternCode} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {revisions.map((revision) => (
                                      <SelectItem key={revision.revision_id} value={revision.revision_id}>
                                        {getRevisionPatternCode(revision)}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-5 w-5 p-0"
                                  onClick={() => {
                                    setEditingVersionNumberId(workingRevision?.revision_id);
                                    setTempVersionNumber(String(workingRevision?.revision_number || ''));
                                  }}
                                  data-testid="pattern-current-version-edit-button"
                                >
                                  <Edit2 className="h-3 w-3" />
                                </Button>
                              </>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0"
                              onClick={() => handleOpenEditNotes(workingRevision)}
                              data-testid="pattern-current-notes-button"
                            >
                              <FileText className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0 text-destructive hover:text-destructive"
                              onClick={() => handleOpenDeleteConfirm(workingRevision)}
                              data-testid="pattern-current-delete-button"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                      {selectedReference && (
                        <div className="bg-slate-50 px-2 py-1.5 text-center text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-600">
                          <div className="flex flex-col items-center gap-0.5">
                            <div className="whitespace-nowrap">{referenceColumnLabel} · {formatCompactDate(selectedReference.created_at)}</div>
                            <Select value={referenceSelection || selectedReference.reference_id} onValueChange={setReferenceSelection}>
                              <SelectTrigger className="h-6 w-[118px] justify-center bg-white text-[11px]" data-testid="pattern-toolbar-reference-version-select">
                                <SelectValue placeholder={referencePatternCode} />
                              </SelectTrigger>
                              <SelectContent>
                                {seedReference && (
                                  <SelectItem value={SEED_REFERENCE_SELECTION}>
                                    Seed Reference · {seedReference.pattern_code || 'Unknown pattern'}
                                  </SelectItem>
                                )}
                                {revisionReferenceOptions.map((revision) => (
                                  <SelectItem key={revision.revision_id} value={revision.revision_id}>
                                    {getRevisionPatternCode(revision)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="divide-y">
                    {specFields.length === 0 ? (
                      <div className="px-4 py-14 text-center text-muted-foreground">
                        <AlertCircle className="mx-auto mb-4 h-10 w-10 opacity-50" />
                        <p>No specification fields configured for {garmentType.label}</p>
                        <p className="mt-1 text-xs">Go to Admin → Pattern Specifications to add fields.</p>
                      </div>
                    ) : (
                      specFields.map((field, fieldIndex) => {
                        const fieldKey = field.field_key;
                        const bodyValue = getBodyMeasurementValue(
                          bodyMeasurementForDisplay,
                          field.linked_measurement_field,
                        );
                        const currentValue = workingDraftSpecs[fieldKey] ?? '';
                        const referenceValue = selectedReference?.garment_specifications?.[fieldKey];
                        const diff = getValueDiff(currentValue, referenceValue);
                        const hasOptions = Array.isArray(field.select_options) && field.select_options.length > 0;
                        const datalistId = hasOptions ? `pattern-datalist-${field.field_id}` : undefined;
                        const specCellBg = fieldIndex % 2 === 0 ? 'bg-white' : 'bg-slate-50/50';

                        return (
                          <div key={field.field_id} className="grid" style={{ gridTemplateColumns: gridColumns }}>
                            <div className={`border-r px-3 py-1.5 ${specCellBg}`}>
                              <div className="flex items-center gap-1.5">
                                <span className="truncate text-[13px] font-medium text-slate-900">{field.field_label}</span>
                                {field.linked_measurement_field && <Link2 className="h-3 w-3 text-amber-500" />}
                              </div>
                            </div>

                            <div className="border-r bg-sky-50/45 px-2 py-1.5 text-center">
                              <span className="font-mono text-[13px] text-slate-600" data-testid={`pattern-body-cell-${fieldKey}`}>
                                {getDisplayValue(bodyValue, field.field_type, unit)}
                              </span>
                            </div>

                            <div className="border-r bg-emerald-50/35 px-2 py-1 text-center">
                              <div className="relative flex min-h-8 items-center justify-center">
                                <Input
                                  ref={(node) => {
                                    if (node) cellInputRefs.current[fieldKey] = node;
                                  }}
                                  type={field.field_type === 'number' ? 'number' : 'text'}
                                  step={field.field_type === 'number' ? '0.25' : undefined}
                                  list={datalistId}
                                  value={currentValue}
                                  onChange={(event) => handleWorkingSpecChange(fieldKey, event.target.value)}
                                  onFocus={(event) => {
                                    setActiveCellFieldKey(fieldKey);
                                    event.target.select?.();
                                  }}
                                  onBlur={() => {
                                    setActiveCellFieldKey(null);
                                    queueAutosave(field);
                                  }}
                                  onKeyDown={(event) => handleWorkingCellKeyDown(event, fieldIndex)}
                                  className={`h-7 ${field.field_type === 'number' ? 'w-[88px] font-mono' : 'w-[112px]'} border px-2 text-center text-[13px] shadow-none transition-colors ${activeCellFieldKey === fieldKey ? 'border-sky-400 bg-white ring-2 ring-sky-200' : 'border-transparent bg-white/70 hover:border-slate-200 focus-visible:ring-0'}`}
                                  data-testid={`pattern-working-cell-input-${fieldKey}`}
                                />
                                {hasOptions && (
                                  <datalist id={datalistId}>
                                    {field.select_options.map((option) => (
                                      <option key={option} value={option} />
                                    ))}
                                  </datalist>
                                )}
                                {diff && (
                                  <div className={`absolute right-1 whitespace-nowrap text-[10px] font-medium ${diff.startsWith('+') ? 'text-emerald-700' : 'text-rose-700'}`}>
                                    {diff.startsWith('+') ? '↑' : '↓'} {diff}
                                  </div>
                                )}
                              </div>
                            </div>

                            {selectedReference && (
                              <div className="bg-slate-50/60 px-2 py-1.5 text-center">
                                <span className="font-mono text-[13px] text-slate-700" data-testid={`pattern-reference-cell-${fieldKey}`}>
                                  {getDisplayValue(referenceValue, field.field_type, unit)}
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {revisions.length > 0 && (
          <div
            className={`sticky self-start flex-shrink-0 transition-all duration-200 ${rightSidebarOpen ? 'w-80' : 'w-12'}`}
            style={{ top: `${stickyOffset}px` }}
          >
            <Card data-testid="pattern-sidebar-card">
              <CardContent className="h-full p-3">
                <div className="mb-3 flex items-center justify-between">
                  {rightSidebarOpen && <h3 className="text-sm font-semibold">Revision Notes</h3>}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => setRightSidebarOpen((open) => !open)}
                    data-testid="pattern-sidebar-toggle-button"
                  >
                    {rightSidebarOpen ? <X className="h-4 w-4" /> : <Info className="h-4 w-4" />}
                  </Button>
                </div>

                {rightSidebarOpen && selectedRevisionForSidebar && (
                  <div className="space-y-3 text-sm" data-testid="pattern-sidebar-content">
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Version</Label>
                      <Select
                        value={selectedRevisionForSidebar.revision_id}
                        onValueChange={(revisionId) => {
                          const matchedRevision = revisions.find((revision) => revision.revision_id === revisionId);
                          setEditingSidebarNotes(false);
                          setSelectedRevisionForSidebar(matchedRevision || null);
                        }}
                      >
                        <SelectTrigger className="mt-1 h-8 text-xs" data-testid="pattern-sidebar-version-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {revisions.map((revision) => (
                            <SelectItem key={revision.revision_id} value={revision.revision_id}>
                              {getRevisionPatternCode(revision)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="rounded-lg border bg-slate-50 px-3 py-2">
                      <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Created</p>
                      <p className="mt-1 text-sm font-medium">{formatCompactDate(selectedRevisionForSidebar.created_at)}</p>
                    </div>

                    {selectedRevisionForSidebar.linked_measurement_id && selectedRevisionForSidebar.body_measurement_snapshot && (
                      <div className="rounded-lg border bg-slate-50 px-3 py-2">
                        <p className="flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                          <Link2 className="h-3 w-3" />
                          Body Measurement
                        </p>
                        <p className="mt-1 text-sm font-medium">
                          {formatCompactDate(selectedRevisionForSidebar.body_measurement_snapshot.measurement_date)}
                        </p>
                        {selectedRevisionForSidebar.body_measurement_snapshot.notes && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            {selectedRevisionForSidebar.body_measurement_snapshot.notes}
                          </p>
                        )}
                      </div>
                    )}

                    <div>
                      <div className="mb-1 flex items-center justify-between">
                        <Label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Notes</Label>
                        {!editingSidebarNotes && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => {
                              setSidebarNotesValue(selectedRevisionForSidebar.revision_notes || '');
                              setEditingSidebarNotes(true);
                            }}
                            data-testid="pattern-sidebar-edit-notes-button"
                          >
                            <Edit2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>

                      {editingSidebarNotes ? (
                        <div className="space-y-2">
                          <Textarea
                            value={sidebarNotesValue}
                            onChange={(event) => setSidebarNotesValue(event.target.value)}
                            rows={5}
                            className="text-sm"
                            placeholder="Add notes..."
                            autoFocus
                            data-testid="pattern-sidebar-notes-textarea"
                          />
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              className="h-7 text-xs"
                              onClick={handleSaveSidebarNotes}
                              disabled={saving}
                              data-testid="pattern-sidebar-save-notes-button"
                            >
                              <Check className="mr-1 h-3 w-3" />
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={() => {
                                setEditingSidebarNotes(false);
                                setSidebarNotesValue('');
                              }}
                              disabled={saving}
                              data-testid="pattern-sidebar-cancel-notes-button"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="rounded-lg border bg-white px-3 py-2">
                          {selectedRevisionForSidebar.revision_notes ? (
                            <p className="whitespace-pre-wrap text-sm">{selectedRevisionForSidebar.revision_notes}</p>
                          ) : (
                            <p className="text-xs italic text-muted-foreground">No notes yet.</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {renderCreatePatternDialog({
        title: `Create ${garmentType.label} Pattern`,
        description: `Pattern will be created with code: ${garmentType.prefix}${customer.customer_no || customer.customer_number || '1'}-${patternForm.customVersionNumber || '1'}`,
        numberInputLabel: 'Version Number (Optional)',
        numberInputTestId: 'pattern-dialog-number-input',
        notesInputLabel: 'Description (Optional)',
        notesInputTestId: 'pattern-dialog-description-input',
        submitButtonTestId: 'pattern-dialog-submit-button',
      })}

      <Dialog open={newRevisionDialogOpen} onOpenChange={setNewRevisionDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Revision</DialogTitle>
            <DialogDescription>
              Duplicate a version and continue editing in the compact compare view.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {allMeasurements?.length > 0 && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                Body measurements linked: {formatCompactDate(bodyMeasurementForDisplay?.measurement_date)}
              </div>
            )}

            {revisions.length > 0 && (
              <div>
                <Label>Copy Measurements From</Label>
                <Select
                  value={revisionForm.copyFromRevisionId || ''}
                  onValueChange={(value) => {
                    const sourceRevision = revisions.find((revision) => revision.revision_id === value);
                    setRevisionForm({
                      ...revisionForm,
                      copyFromRevisionId: value,
                      garment_specifications: sourceRevision ? { ...sourceRevision.garment_specifications } : {},
                    });
                  }}
                >
                  <SelectTrigger className="mt-1" data-testid="pattern-new-revision-copy-select">
                    <SelectValue placeholder="Select version to duplicate..." />
                  </SelectTrigger>
                  <SelectContent>
                    {revisions.map((revision) => (
                      <SelectItem key={revision.revision_id} value={revision.revision_id}>
                        {getRevisionPatternCode(revision)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label>Custom Version Number (Optional)</Label>
              <Input
                type="number"
                placeholder={`Auto-increment: ${(revisions[0]?.revision_number || 0) + 1}`}
                value={revisionForm.customVersionNumber}
                onChange={(event) => setRevisionForm({ ...revisionForm, customVersionNumber: event.target.value })}
                className="mt-1"
                data-testid="pattern-new-revision-number-input"
              />
            </div>
            <div>
              <Label>Revision Notes (Optional)</Label>
              <Textarea
                placeholder="Describe what changed..."
                value={revisionForm.revision_notes}
                onChange={(event) => setRevisionForm({ ...revisionForm, revision_notes: event.target.value })}
                rows={3}
                className="mt-1"
                data-testid="pattern-new-revision-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewRevisionDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleCreateRevision} disabled={saving} data-testid="pattern-new-revision-submit-button">
              {saving ? 'Creating...' : 'Create Revision'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editNotesDialogOpen} onOpenChange={setEditNotesDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Revision Notes</DialogTitle>
            <DialogDescription>
              {revisionToEditNotes && `Update notes for ${getRevisionPatternCode(revisionToEditNotes)}`}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Notes</Label>
            <Textarea
              placeholder="Add notes about this revision..."
              value={editedNotes}
              onChange={(event) => setEditedNotes(event.target.value)}
              rows={6}
              className="mt-2"
              data-testid="pattern-edit-notes-textarea"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditNotesDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSaveNotes} disabled={saving} data-testid="pattern-edit-notes-save-button">
              {saving ? 'Saving...' : 'Save Notes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Revision</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this revision? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {revisionToDelete && (
            <div className="py-4">
              <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-4">
                <div className="mb-2 flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-destructive" />
                  <span className="font-semibold text-destructive">{getRevisionPatternCode(revisionToDelete)}</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Created: {format(new Date(revisionToDelete.created_at), 'MMMM d, yyyy')}
                </p>
                {revisionToDelete.revision_notes && (
                  <p className="mt-2 text-sm">Notes: {revisionToDelete.revision_notes}</p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteRevision} disabled={saving} data-testid="pattern-delete-confirm-button">
              {saving ? 'Deleting...' : 'Delete Revision'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PatternTab;